// Live dashboard — real KPIs, quick actions, recent activity feed.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    BedDouble, Users, ClipboardList, CalendarDays, Image as ImgIcon, Shield,
    TrendingUp, ArrowRight, DollarSign, Building, PlusCircle, BarChart3, FileText
} from "lucide-react";
import { useAdminAuth, adminApi } from "@/lib/adminAuth";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const usd = (n) => `$${Number(n || 0).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;

function Kpi({ label, value, hint, testid, accent }) {
    return (
        <div data-testid={testid} className={`bg-white border border-navy/10 rounded-2xl p-5 ${accent ? "ring-1 ring-gold/40" : ""}`}>
            <div className="text-[10px] uppercase tracking-[0.22em] text-navy/60">{label}</div>
            <div className="mt-2 font-display text-4xl text-navy">{value}</div>
            {hint && <div className="text-[11px] text-navy/50 mt-1">{hint}</div>}
        </div>
    );
}

function QuickAction({ to, icon: Icon, label, desc }) {
    return (
        <Link to={to} className="group bg-white border border-navy/10 rounded-2xl p-5 flex items-start gap-4 hover:border-gold/60 transition-colors">
            <div className="w-10 h-10 rounded-full bg-navy/5 flex items-center justify-center text-navy group-hover:bg-gold/15 group-hover:text-gold transition-colors">
                <Icon size={16} />
            </div>
            <div className="flex-1">
                <div className="font-medium text-navy">{label}</div>
                <div className="text-[11px] text-navy/55 mt-0.5">{desc}</div>
            </div>
            <ArrowRight size={14} className="text-navy/30 group-hover:text-gold transition-colors mt-1" />
        </Link>
    );
}

export default function AdminDashboard() {
    const { user } = useAdminAuth();
    const [stats, setStats] = useState(null);
    const [staffCount, setStaffCount] = useState(null);
    const [recent, setRecent] = useState([]);

    useEffect(() => {
        adminApi.get("/admin/reservations/stats").then((r) => setStats(r.data)).catch(() => {});
        adminApi.get("/admin/staff").then((r) => setStaffCount(r.data.length)).catch(() => {});
        adminApi.get("/admin/reservations?limit=6").then((r) => setRecent(r.data)).catch(() => {});
        // Public rooms count fallback
        axios.get(`${API}/rooms`).catch(() => {});
    }, []);

    const rev = stats?.revenue_paid_this_month;
    const canSeeStaff = user?.role === "super_admin";

    return (
        <div data-testid="admin-dashboard">
            <header className="mb-8">
                <div className="text-[10px] uppercase tracking-[0.28em] text-gold">Dashboard</div>
                <h1 className="font-display text-4xl md:text-5xl text-navy mt-3">Welcome back, {user?.name?.split(" ")[0] || "Admin"}.</h1>
                <p className="text-navy/60 mt-2 text-sm">Reservations, payments, occupancy and guest activity — live from the resort database.</p>
            </header>

            {/* Top-line KPIs */}
            <section className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <Kpi label="Arrivals today" value={stats?.arrivals_today ?? "—"} hint="Guests checking in" testid="kpi-arrivals" accent />
                <Kpi label="Departures today" value={stats?.departures_today ?? "—"} hint="Guests checking out" testid="kpi-departures" />
                <Kpi label="In-house" value={stats?.in_house ?? "—"} hint="Currently staying" testid="kpi-inhouse" />
                <Kpi label="Occupancy" value={stats ? `${stats.occupancy_pct}%` : "—"} hint={stats ? `${stats.occupied_today}/${stats.published_rooms} rooms` : "Live"} testid="kpi-occupancy" />
            </section>

            <section className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
                <Kpi label="Pending" value={stats?.pending ?? "—"} hint="Awaiting confirmation" testid="kpi-pending" />
                <Kpi label="Upcoming" value={stats?.upcoming ?? "—"} hint="Future check-ins" testid="kpi-upcoming" />
                <Kpi label="Revenue (month)" value={stats ? usd(rev) : "—"} hint="Received this month" testid="kpi-revenue-month" />
                <Kpi label="Total revenue" value={stats ? usd(stats.revenue_paid_all_time) : "—"} hint="All-time received" testid="kpi-revenue-total" />
            </section>

            {/* Inventory snapshot */}
            <section className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
                <Kpi label="Total rooms" value={stats?.total_rooms ?? "—"} hint={`${stats?.published_rooms ?? "—"} published`} testid="kpi-rooms" />
                <Kpi label="Available today" value={stats?.available_today ?? "—"} hint="Bookable now" testid="kpi-available" />
                <Kpi label="Blocked" value={stats?.blocked_today ?? "—"} hint="Maintenance / hold" testid="kpi-blocked" />
                <Kpi label="Staff accounts" value={staffCount ?? "—"} hint={canSeeStaff ? "Active admins" : "Super Admin only"} testid="kpi-staff" />
            </section>

            {/* Quick actions */}
            <section className="mb-10">
                <div className="text-[10px] uppercase tracking-[0.22em] text-navy/50 mb-4">Quick actions</div>
                <div className="grid md:grid-cols-2 gap-4">
                    <QuickAction to="/admin/reservations/new" icon={PlusCircle} label="Add reservation" desc="Manually record a booking" />
                    <QuickAction to="/admin/calendar" icon={CalendarDays} label="Booking calendar" desc="Visual room availability day-by-day" />
                    <QuickAction to="/admin/reservations" icon={ClipboardList} label="Reservations" desc="Search, edit, cancel and record payments" />
                    <QuickAction to="/admin/rooms" icon={BedDouble} label="Manage rooms" desc="Rates, descriptions, published status" />
                    <QuickAction to="/admin/guests" icon={Users} label="Guest database" desc="History, contact details, total spend" />
                    <QuickAction to="/admin/media" icon={ImgIcon} label="Media library" desc="Upload and organise room photos" />
                    <QuickAction to="/admin/reports" icon={BarChart3} label="Reports & exports" desc="Revenue, occupancy, CSV downloads" />
                    {canSeeStaff && <QuickAction to="/admin/audit-log" icon={FileText} label="Audit log" desc="Every administrative change, logged" />}
                    {canSeeStaff && <QuickAction to="/admin/staff" icon={Shield} label="Staff accounts" desc="Invite, edit roles, disable access" />}
                </div>
            </section>

            {/* Recent reservations */}
            <section className="mb-10">
                <div className="flex items-center justify-between mb-4">
                    <div className="text-[10px] uppercase tracking-[0.22em] text-navy/50">Recent reservations</div>
                    <Link to="/admin/reservations" className="text-[11px] uppercase tracking-[0.16em] text-navy/70 hover:text-gold inline-flex items-center gap-1">View all <ArrowRight size={11} /></Link>
                </div>
                <div className="bg-white border border-navy/10 rounded-2xl overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-navy/5 text-[10px] uppercase tracking-[0.18em] text-navy/60">
                            <tr>
                                <th className="text-left px-4 py-3">Guest</th>
                                <th className="text-left px-4 py-3 hidden md:table-cell">Room</th>
                                <th className="text-left px-4 py-3">Dates</th>
                                <th className="text-left px-4 py-3">Status</th>
                                <th className="text-right px-4 py-3">Total</th>
                            </tr>
                        </thead>
                        <tbody>
                            {recent.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-navy/50">No reservations yet</td></tr>}
                            {recent.map((r) => (
                                <tr key={r.id} className="border-t border-navy/10">
                                    <td className="px-4 py-3">
                                        <Link to={`/admin/reservations/${r.id}`} className="text-navy hover:text-gold">
                                            {r.guest_name || "—"}
                                        </Link>
                                        <div className="text-[11px] text-navy/50">{r.confirmation_number}</div>
                                    </td>
                                    <td className="px-4 py-3 hidden md:table-cell text-navy/70">{r.room_name || r.room_slug || "—"}</td>
                                    <td className="px-4 py-3 text-navy/70 text-[12px]">{r.check_in} → {r.check_out}</td>
                                    <td className="px-4 py-3">
                                        <span className="text-[10px] uppercase tracking-[0.14em] px-2 py-1 rounded-full bg-navy/10 text-navy/70">{r.status?.replace("_", " ")}</span>
                                    </td>
                                    <td className="px-4 py-3 text-right font-display text-navy">{usd(r.total)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </section>

            {/* Phase progress banner */}
            <section className="rounded-2xl bg-gradient-to-br from-navy to-navy/90 text-ivory p-8 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-64 h-64 bg-gold/15 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3" />
                <div className="relative">
                    <div className="text-[10px] uppercase tracking-[0.24em] text-gold flex items-center gap-2"><TrendingUp size={12} /> Live</div>
                    <h2 className="font-display text-3xl mt-3">Serenity Cove PMS · <span className="text-gold">Operational</span></h2>
                    <p className="text-ivory/70 text-sm mt-3 max-w-xl leading-relaxed">
                        You're signed in as <span className="text-gold">{user?.role?.replace("_", " ")}</span>. All core modules — reservations, calendar, rooms, guests, media, reports and audit log — are wired to the live database.
                    </p>
                </div>
            </section>
        </div>
    );
}
